import {
  AbstractClient,
  ArticlesMessage,
  ButtonOptionsConversationMessage,
  Client,
  ClientErrorResponse,
  ClientResponse,
  ClientStatus,
  ClientUrls,
  Conversation,
  ConversationActionParams,
  ConversationChannel,
  ConversationMessage,
  ConversationParticipant,
  ConversationStatus,
  ConversationSyncStorageMode,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsResponse,
  InlineDataConversationMessage,
  InlineDatum as ConversationInlineDatum,
  InProgressParams,
  InProgressResponse,
  isNetworkError,
  isOnline,
  LeaveConversationParams,
  ObservabilityConfig,
  RichTextConversationMessage,
  SelectOptionAction,
  SendConversationMessageParams,
  StartConversationParams,
  StartConversationResponse,
  TextConversationMessage,
  TextWithAttachmentsConversationMessage,
  InlineDataResultAction,
  ConversationInlineData,
  ButtonsMessage,
  ArticlesConversationMessage,
  ConversationMessageStatus,
  StreamTextParams,
  StreamTextResponse,
  ErrorScope,
  SendFeedbackParams,
} from '@stripe-internal/conversations-client';
import LRUCache from 'lru-cache';
import {
  ANIMATION_DURATION,
  shouldHideMessageAfterThumbsDownAdvance,
  isButtonOptionsMessage,
  isInlineDataMessage,
  ParentMessageBus,
} from '@stripe-internal/conversations-ui';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  firstValueFrom,
  Observable,
} from 'rxjs';
import {createParentMessageBus} from '@stripe-internal/conversations-ui/dist/Widget/iframe';
import {
  UniversalHelpAdvanceEndpointRequest,
  UniversalHelpAdvanceEndpointResponse,
  UniversalHelpAdvanceEndpointResponseClientError,
  UniversalHelpAdvanceEndpointResponseError,
  UniversalHelpAdvanceEndpointResponseSuccess,
  UniversalHelpCreateEndpointRequest,
  UniversalHelpCreateEndpointResponse,
  UniversalHelpCreateEndpointResponseError,
  UniversalHelpCreateEndpointResponseSuccess,
  UniversalHelpEffect,
  UniversalHelpEffectContinueImmediately,
  UniversalHelpEndEndpointResponseError,
  UniversalHelpEndEndpointResponseSuccess,
  UniversalHelpInProgressEndpointResponse,
  UniversalHelpInProgressEndpointResponseConversationExists,
  UniversalHelpInProgressEndpointResponseError,
  UniversalHelpInProgressEndpointResponseNoConversation,
  UniversalHelpStripeAssistantEndpointResponse,
  UniversalHelpResponseFeedbackEndpointRequest,
} from './generated/types';
import {METRIC} from './metrics';

export const UniversalHelpUser: ConversationParticipant = {
  id: 'uh-bot',
  role: 'bot',
  displayName: 'Stripe Support',
};

const anonUser: ConversationParticipant = {
  id: 'anonymous-user',
  role: 'user',
  displayName: 'Anonymous User',
};

const CLIENT_TYPE = 'universal_help';
const METRIC_TAGS = {client_type: CLIENT_TYPE};
const ERROR_TAGS = METRIC_TAGS;

const KB = 1024;

const UNAUTHORIZED_ERROR = 'Unauthorized';
const UNAUTHORIZED_RESPONSE = {
  code: 401,
  type: 'error',
  error: UNAUTHORIZED_ERROR,
} as const;

const ANIMATION_DURATION_WITH_BUFFER = ANIMATION_DURATION + 100;
const PARAMS_NOT_TO_FORWARD_TO_SUPPORT_CONVERSATIONS = ['forestVersion'];

const ANONYMOUS_TOKEN_PREFIX = 'scs-noauth_';

type StartInProgressConversationParams = StartConversationParams & {
  inProgressResponse?: InProgressResponse;
};

type UniversalHelpEndEndpointResponseErrorWithCode =
  UniversalHelpEndEndpointResponseError & {
    code: number;
  };

export type UniversalHelpClientOptions = {
  clientFetch?: typeof fetch;
  urls?: Partial<ClientUrls>;
  observability?: ObservabilityConfig;
  user?: ConversationParticipant;
  waitForAnimationEnabled?: boolean;
  userCountry?: string;
  enableRegionHeaderRouting?: boolean;
  enableUhPendingInput?: boolean;
};

export class UniversalHelpClient extends AbstractClient implements Client {
  protected user: ConversationParticipant;

  channel: ConversationChannel = 'bot';

  type: string = CLIENT_TYPE;

  syncStorageMode: ConversationSyncStorageMode = 'sync';

  pending$ = new BehaviorSubject<boolean>(false);

  waitForAnimationEnabled = true;

  // Whether the user is logged in (anonymous user IDs start with 'scs-noauth_')
  protected isLoggedIn = false;

  protected messageCache: LRUCache<string, ConversationMessage> = new LRUCache({
    max: 500,
    maxSize: 512 * KB,
    sizeCalculation: (o) =>
      Object.values(o).reduce((acc, p) => acc + (p?.length || 1), 0),
  });

  parentMessageBus: ParentMessageBus;

  protected userCountry?: string;

  protected enableRegionHeaderRouting: boolean;

  protected enableUhPendingInput: boolean;

  constructor({
    clientFetch,
    urls,
    observability,
    user,
    waitForAnimationEnabled = true,
    userCountry,
    enableRegionHeaderRouting = false,
    enableUhPendingInput = false,
  }: UniversalHelpClientOptions = {}) {
    super({clientFetch, urls, observability});

    this.user = user || anonUser;
    this.userCountry = userCountry;
    this.enableRegionHeaderRouting = enableRegionHeaderRouting;
    this.waitForAnimationEnabled = waitForAnimationEnabled;
    this.enableUhPendingInput = enableUhPendingInput;

    this.status$.next(
      isOnline() ? ClientStatus.Connected : ClientStatus.Offline,
    );

    // since we don't have a socket we're reconnecting,
    // when we come back online set the status to connected
    this.status$.pipe(debounceTime(50)).subscribe(() => {
      if (this.getStatus() === ClientStatus.Reconnecting) {
        this.status$.next(ClientStatus.Connected);
      }
    });
    this.parentMessageBus = createParentMessageBus({
      parentOrigin: '*',
    });
  }

  async disconnect(): Promise<void> {
    // no-op
  }

  async startConversation(
    params: StartInProgressConversationParams,
  ): Promise<StartConversationResponse> {
    try {
      const {
        conversationId,
        participant: user,
        context,
        syncStorage = this.syncStorageMode,
        connect = true,
        fetch = true,
      } = params;

      if (!user) {
        return this.error(400, 'Missing user');
      }
      this.user = user;
      // Anonymous user IDs start with 'scs-noauth_' prefix
      this.isLoggedIn = !user.id.startsWith(ANONYMOUS_TOKEN_PREFIX);

      if (!context || !context.audience) {
        return this.error(400, 'Missing audience');
      }

      if (!context || !context.entryPoint) {
        return this.error(400, 'Missing entrypoint');
      }

      const {audience, entryPoint} = context;

      const urlSearchParams = new URLSearchParams(window.location.search);
      const forestVersion = urlSearchParams.get('forestVersion') || undefined;

      let id: string;
      let uhEffects: Array<UniversalHelpEffect>;

      let inProgressResp:
        | UniversalHelpInProgressEndpointResponse
        | undefined
        | null;
      inProgressResp = this.validExistingInProgressResponse(params);

      if (!inProgressResp) {
        inProgressResp = await this.inProgressConversation({
          conversationId,
          audience,
          entryPoint,
          triggerPoint: context.triggerParams?.triggerPoint,
        });
      }

      if (conversationId && inProgressResp.type === 'no_conversation') {
        return {code: 404, error: `Conversation ${conversationId} not found`};
      } else if (inProgressResp.type === 'conversation_exists') {
        if (
          conversationId &&
          conversationId !== inProgressResp.conversationId
        ) {
          return {code: 404, error: `Conversation ${conversationId} not found`};
        }
        const {conversationId: inProgressId, effects} = inProgressResp;
        id = inProgressId;
        uhEffects = effects;
      } else if (
        inProgressResp.type === 'error' &&
        inProgressResp.error === UNAUTHORIZED_ERROR
      ) {
        return {code: 401, error: UNAUTHORIZED_ERROR};
      } else {
        const additionalParams: {[key: string]: string} = {};
        Array.from(urlSearchParams.entries())
          .filter(
            ([key]) =>
              !PARAMS_NOT_TO_FORWARD_TO_SUPPORT_CONVERSATIONS.includes(key),
          )
          .forEach(([key, value]) => {
            additionalParams[key] = value;
          });

        // Always include triggerPoint and initialQuery as additionalParams.
        // For now, only include refcode if it is not already defined in the
        // urlSearchParams.
        if (context.triggerParams?.refcode && !additionalParams.refcode) {
          additionalParams.refcode = context.triggerParams.refcode;
        }

        if (
          context.assistantAccessibleViaSearch &&
          !additionalParams.assistant_accessible_via_search
        ) {
          additionalParams.assistant_accessible_via_search = String(
            context.assistantAccessibleViaSearch,
          );
        }

        const resp = await this.createConversation({
          // hard-coded for now, ignoring the param
          audience: audience as UniversalHelpCreateEndpointRequest['audience'],
          forestVersion,
          entryPoint:
            entryPoint as UniversalHelpCreateEndpointRequest['entryPoint'],
          ...(context.triggerParams ?? null),
          data_with_pii: params.data,
          additionalParams,
          // Using this `as` due to the triggerPoint param being a string in the triggerParams
          // object. We should fix this later to use the proper type.
        } as UniversalHelpCreateEndpointRequest);
        if (resp.type === 'error') {
          if (resp.error === UNAUTHORIZED_ERROR) {
            return {code: 401, error: UNAUTHORIZED_ERROR};
          }
          // this should be unreachable since the underlying call should already throw
          throw new Error(`Error starting new conversation: ${resp.error}`);
        }
        const {conversationId: inProgressId, effects} = resp;
        id = inProgressId;
        uhEffects = effects;
      }

      const lastEffect = uhEffects[uhEffects.length - 1];
      let enabledInputModes = ['text', 'prompt-option'];
      if (
        lastEffect.type === 'wait_for_response' &&
        !lastEffect.allowTextInput
      ) {
        enabledInputModes = ['prompt-option'];
      }
      context.enabledInputModes = enabledInputModes;

      const messages = await this.effectsToMessages(id, uhEffects);

      const conversation = new Conversation(this, {
        id,
        user,
        messages,
        participants: {
          [user.id]: user,
          [UniversalHelpUser.id]: UniversalHelpUser,
        },
        syncStorage,
        context: {...context},
      });

      if (connect) {
        await conversation.connect({fetch: false});
      }

      if (fetch) {
        await this.handleMostRecentEffect(id, uhEffects);
      }

      this.incrementAndTrack(METRIC.start_conversation_success, {
        universalHelpConversationId: id,
        audience,
      });

      return {code: 200, data: {conversation}};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = await this.reportAndEmitException(
        `exception starting conversation: ${(e as Error).message}`,
      );

      this.incrementAndTrack(METRIC.start_conversation_error);

      return clientErrorResponse;
    }
  }

  async leaveConversation({
    conversationId,
  }: LeaveConversationParams): Promise<ClientResponse> {
    const response = await this.endConversation(conversationId);
    if (response.type === 'error') {
      return {code: response.code, error: response.error};
    }
    return {code: 200};
  }

  async getConversationMessages({
    conversationId,
    messageIds,
  }: GetConversationMessagesParams): Promise<GetConversationMessagesResponse> {
    try {
      const cachedMessages: Record<string, ConversationMessage> = {};
      let cachedCount = 0;
      if (messageIds) {
        messageIds.forEach((messageId) => {
          const cachedMessage = this.getCachedMessage(
            conversationId,
            messageId,
          );
          if (cachedMessage) {
            cachedCount += 1;
            cachedMessages[cachedMessage.id] = cachedMessage;
          }
        });
      }

      if (messageIds && cachedCount === messageIds.length) {
        return {code: 200, data: {messages: cachedMessages}};
      }

      const inProgressResp = await this.inProgressConversation({
        conversationId,
      });
      if (
        inProgressResp.type === 'error' &&
        inProgressResp.error === UNAUTHORIZED_ERROR
      ) {
        return {code: 401, error: UNAUTHORIZED_ERROR};
      }
      if (inProgressResp.type !== 'conversation_exists') {
        return {code: 200, data: {messages: {}}};
      }

      const {effects} = inProgressResp;

      const messages = await this.effectsToMessages(conversationId, effects);

      return {code: 200, data: {messages}};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = await this.reportAndEmitException(
        `exception getting conversation messages: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async getConversationParticipants(): Promise<GetConversationParticipantsResponse> {
    return {
      code: 200,
      data: {
        participants: {
          [UniversalHelpUser.id]: UniversalHelpUser,
          [this.user.id]: this.user,
        },
      },
    };
  }

  async sendConversationMessage({
    conversationId,
    message,
  }: SendConversationMessageParams): Promise<ClientResponse> {
    this.pending$.next(true);
    const result = await this.handleSendConversationMessage({
      conversationId,
      message,
    });
    this.pending$.next(false);
    return result;
  }

  private async handleSendConversationMessage({
    conversationId,
    message,
  }: SendConversationMessageParams): Promise<ClientResponse> {
    if (message.type === 'text-with-attachments-conversation-message') {
      return this.handleSendTextWithAttachmentsMessage({
        conversationId,
        message: message as TextWithAttachmentsConversationMessage,
      });
    }

    return this.handleSendTextMessage({
      conversationId,
      message: message as TextConversationMessage,
    });
  }

  private async handleSendTextMessage({
    conversationId,
    message,
  }: {
    conversationId: string;
    message: TextConversationMessage;
  }): Promise<ClientResponse> {
    try {
      const resp = await this.advanceConversation(conversationId, {
        optionId: 'text-input',
        message: message.text,
        data_with_pii: message.data,
      });

      if (resp.type === 'invalid-option-for-conversation') {
        return this.handleInvalidOption(conversationId, message.id);
      }

      if (resp.type === 'error') {
        if (resp.error === UNAUTHORIZED_ERROR) {
          return {code: 401, error: resp.error};
        }
        return {code: 400, error: resp.error};
      }

      if (resp.type === 'success') {
        const optionSelectedEffect = resp.effects.find((effect) => {
          return (
            effect.type === 'option_was_selected' &&
            effect.optionId === 'text-input' &&
            effect.messageOverride === message.text
          );
        });

        if (optionSelectedEffect) {
          this.emitMessage(
            conversationId,
            {
              ...message,
              id: optionSelectedEffect.id,
              status: ConversationMessageStatus.Sent,
            },
            'conversation-message-updated',
          );
        } else {
          this.errors.reportError(
            'no option_was_select effect returned in text-input request',
            {
              tags: {
                conversationId,
              },
            },
          );
        }

        if (this.enableUhPendingInput) {
          await this.handleEffects(conversationId, resp.effects, true);
        } else {
          this.handleEffects(conversationId, resp.effects, true);
        }
        return {code: 200};
      } else {
        return {code: 400, error: resp.type};
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = await this.reportAndEmitException(
        `exception sending conversation message: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  private async handleSendTextWithAttachmentsMessage({
    conversationId,
    message,
  }: {
    conversationId: string;
    message: TextWithAttachmentsConversationMessage;
  }): Promise<ClientResponse> {
    try {
      const attachments = message.attachments
        .filter((a) => !!a.upload_token)
        .map((a) => ({
          name: a.name,
          size: a.size,
          type: a.type,
          upload_token: a.upload_token!,
        }));

      const resp = await this.advanceConversation(conversationId, {
        optionId: 'text-with-attachments-input',
        message: message.text,
        attachments,
        data_with_pii: message.data,
      });

      if (resp.type === 'invalid-option-for-conversation') {
        return this.handleInvalidOption(conversationId, message.id);
      }

      if (resp.type === 'error') {
        if (resp.error === UNAUTHORIZED_ERROR) {
          return {code: 401, error: resp.error};
        }
        return {code: 400, error: resp.error};
      }

      if (resp.type === 'success') {
        const optionSelectedEffect = resp.effects.find((effect) => {
          return (
            effect.type === 'option_was_selected' &&
            effect.optionId === 'text-with-attachments-input' &&
            effect.messageOverride === message.text
          );
        });

        if (optionSelectedEffect) {
          this.emitMessage(
            conversationId,
            {
              ...message,
              id: optionSelectedEffect.id,
              status: ConversationMessageStatus.Sent,
            },
            'conversation-message-updated',
          );
        } else {
          this.errors.reportError(
            'no option_was_selected effect returned in text-with-attachments request',
            {
              tags: {
                conversationId,
              },
            },
          );
        }

        this.handleEffects(conversationId, resp.effects, true);
        return {code: 200};
      } else {
        return {code: 400, error: resp.type};
      }
    } catch (e) {
      if (isNetworkError(e)) {
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = await this.reportAndEmitException(
        `exception sending conversation message: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async sendConversationTyping(): Promise<ClientResponse> {
    return {code: 200};
  }

  async streamText({
    conversationId,
    saResponseId,
    effectId,
    streamId,
    offset,
    cacheUuid,
    signal,
  }: StreamTextParams): Promise<StreamTextResponse> {
    const params = new URLSearchParams();

    params.set('conversation_id', conversationId);
    params.set('uh_conversation_id', conversationId);
    params.set('sa_response_id', saResponseId);
    params.set('llm_conversation_id', streamId);
    params.set('offset', offset.toString());
    params.set('cache_uuid', cacheUuid);
    params.set('effect_id', effectId);

    let fetched;
    try {
      fetched = await this.fetch(
        `${this.conversationUrl}/stripe-assistant?${params.toString()}`,
        {signal},
      );
    } catch (e) {
      if (isNetworkError(e)) {
        return {code: 408, error: (e as Error).message};
      }

      const error = `exception streaming text: ${(e as Error).message}`;
      this.emitNewEvent({
        name: 'client-exception',
        error,
      });
      return {code: 500, error};
    }

    if (fetched.status === 401) {
      return UNAUTHORIZED_RESPONSE;
    }

    const data: UniversalHelpStripeAssistantEndpointResponse =
      await fetched.json();
    if (data.type === 'error') {
      // unless this is a 401, something went wrong; throw
      if (fetched.status !== 401) {
        throw new Error(`Error streaming text: ${data.error}`);
      }
    }

    return {code: 200, data};
  }

  async inProgress(params: InProgressParams): Promise<InProgressResponse> {
    const {conversationId, context} = params;

    // we're explicitly NOT using the passed conversationId as the default
    // channelConversationId here.
    let channelConversationId: string | undefined;
    if (context?.channel) {
      // params will include any ConversationContext, so check for
      // channel-specific conversation id
      channelConversationId = context[`${context.channel}_conversationId`] as
        | string
        | undefined;
    }

    const channelParams = {
      ...params,
      // when we check individual channels, use the channel-specific id
      conversationId: channelConversationId || conversationId,
    };

    // when we're returning a response from this call, if we're returning
    // inProgress for a given conversation id, we should return that id
    const responseConversationId = conversationId || channelConversationId;

    try {
      const resp = await this.inProgressConversation({
        conversationId: channelConversationId,
        audience: context?.audience,
        entryPoint: context?.entryPoint,
        triggerPoint: context?.triggerParams?.triggerPoint,
      });
      if (resp.type === 'no_conversation') {
        return {code: 200, data: {inProgress: false, clientType: CLIENT_TYPE}};
      } else if (resp.type === 'error' && resp.error === UNAUTHORIZED_ERROR) {
        return {code: 401, error: UNAUTHORIZED_ERROR};
      }
      let uhResponseConverastionId: string | undefined;
      let effects: Array<UniversalHelpEffect> | undefined;
      if (resp.type === 'conversation_exists') {
        uhResponseConverastionId = resp.conversationId;
        effects = resp.effects;
      }
      return {
        code: 200,
        data: {
          ...channelParams,
          conversationId: uhResponseConverastionId ?? responseConversationId,
          clientType: CLIENT_TYPE,
          inProgress: true,
          extra: {effects},
        },
      };
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = this.reportAndEmitException(
        `exception checking in-progress: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async action({
    conversationId,
    action,
  }: ConversationActionParams): Promise<ClientResponse> {
    this.pending$.next(true);
    const result = await this.handleAction({conversationId, action});
    this.pending$.next(false);
    return result;
  }

  private async handleAction({
    conversationId,
    action,
  }: ConversationActionParams): Promise<ClientResponse> {
    try {
      if (action.name === 'get-smart-suggestions') {
        const resp = await this.advanceConversation(conversationId, {
          optionId: action.name,
          data_with_pii: action.data ?? undefined,
        });

        if (resp.type === 'success') {
          if (this.enableUhPendingInput) {
            await this.handleEffects(conversationId, resp.effects, true);
          } else {
            this.handleEffects(conversationId, resp.effects, true);
          }
        } else if (resp.type === 'error' && resp.error === UNAUTHORIZED_ERROR) {
          return {code: 401, error: resp.error};
        } else {
          return {code: 400, error: resp.type};
        }
      } else if (action.name === 'select-option') {
        const {message, selectedOptionId} = action as SelectOptionAction;

        this.emitNewConversationEvent({
          name: 'conversation-option-selected',
          conversationId,
          optionId: selectedOptionId,
        });

        if (message.purpose) {
          this.emitNewConversationEvent({
            name: 'conversation-set-context-values',
            conversationId,
            values: {[message.purpose]: selectedOptionId},
          });
        }

        if (selectedOptionId === 'text-input') {
          return this.error(400, 'text-input option is not valid here');
        }
        if (selectedOptionId === 'text-with-attachments-input') {
          return this.error(
            400,
            'text-with-attachments-input option is not valid here',
          );
        }

        if (selectedOptionId.startsWith('link_account_delete_option_reason_')) {
          this.incrementAndTrack(METRIC.link_accounts_deleted, {
            universalHelpConversationId: conversationId,
            reason: selectedOptionId,
          });
        }

        const selectedOption = message.options.find(
          (o) => o.id === selectedOptionId,
        );
        if (selectedOption?.clickInterceptUrl) {
          // Request the parent window to handle the redirect safely
          this.parentMessageBus.post({
            type: 'request-url-redirect',
            data: {
              url: selectedOption.clickInterceptUrl,
            },
          });
          // Emit new typing started event to show caret while redirect page loads
          this.emitNewConversationEvent({
            name: 'conversation-typing-started',
            conversationId,
            participantId: UniversalHelpUser.id,
          });
          return {code: 200};
        }

        const resp = await this.advanceConversation(conversationId, {
          optionId: selectedOptionId,
          message: selectedOption?.label,
          data_with_pii: action.data ?? undefined,
        });

        if (resp.type === 'invalid-option-for-conversation') {
          return this.handleInvalidOption(conversationId, message.id);
        }

        if (resp.type === 'success') {
          if (
            resp.effects.length > 0 &&
            resp.effects[0].type === 'option_was_selected'
          ) {
            const {optionId} = resp.effects[0];
            if (optionId !== selectedOptionId) {
              // I don't expect this to ever happen, but I want to know if it does
              this.errors.reportError(
                'selectedOptionId mismatch with option_was_selected optionId',
                {
                  tags: {
                    optionId,
                    selectedOptionId,
                  },
                },
              );
            }
          }

          const cachedMessage = this.getCachedMessage(
            conversationId,
            message.id,
          );
          if (
            cachedMessage &&
            cachedMessage.type === 'button-options-conversation-message'
          ) {
            this.emitMessage(
              conversationId,
              {
                ...cachedMessage,
                selectedOptionId,
                status: ConversationMessageStatus.Sent,
              },
              'conversation-message-updated',
            );
          }

          if (this.enableUhPendingInput) {
            await this.handleEffects(conversationId, resp.effects, true);
          } else {
            this.handleEffects(conversationId, resp.effects, true);
          }
        } else if (resp.type === 'error' && resp.error === UNAUTHORIZED_ERROR) {
          return {code: 401, error: resp.error};
        } else {
          return {code: 400, error: resp.type};
        }
      } else if (action.name === 'inline-data-result') {
        const {message, result} = action as InlineDataResultAction;
        if (!message.inlineData.isDone) {
          let optionId = 'error';
          if (result.type === 'success') {
            optionId = 'done';
          }

          const extra = result.extra ?? {};
          const outcome = extra?.outcome ?? result.data?.outcome;
          const dataWithPii = extra?.dataWithPii ?? result.data?.dataWithPii;

          const cachedMessage = this.getCachedMessage(
            conversationId,
            message.effectId,
          );

          let resp:
            | UniversalHelpAdvanceEndpointResponseSuccess
            | UniversalHelpAdvanceEndpointResponseClientError
            | UniversalHelpAdvanceEndpointResponseError;
          if (outcome === 'channel_rac' || outcome === 'channel_email') {
            // if we submitted a form via inline data, don't advance
            // we may later decide we do want to advance and let the server
            // effect end the conversation
            resp = {type: 'success', effects: []};
          } else {
            resp = await this.advanceConversation(
              conversationId,
              {
                optionId,
                message: result.message,
                inline_data_outcome: outcome,
                data_with_pii: dataWithPii,
              },
              {avoidTyping: true},
            );
          }

          if (resp.type === 'invalid-option-for-conversation') {
            return this.handleInvalidOption(conversationId, message.effectId);
          }

          if (resp.type === 'success') {
            // update the message and set it as done.
            if (cachedMessage) {
              let {data} = message.inlineData;
              // if the result included the current value, update with it
              if (
                cachedMessage &&
                Array.isArray(message.inlineData.data) &&
                message.inlineData.data.length === 1 &&
                result.extra &&
                (result.extra?.value || result.data)
              ) {
                const value =
                  result.extra.value ??
                  (result.data as Record<string, unknown>);
                data = message.inlineData.data.map((datum) => {
                  const {value: datumValue} = datum;
                  return {
                    ...datum,
                    value: {
                      ...(datumValue ?? {}),
                      ...value,
                    },
                  };
                });
              }

              const inlineData = {
                ...message.inlineData,
                isDone: true,
                data,
              };

              let msg: ConversationMessage;
              if ('data' in cachedMessage) {
                msg = {
                  ...cachedMessage,
                  data: {
                    ...message,
                    inlineData,
                  },
                } as RichTextConversationMessage;
              } else {
                msg = {
                  ...cachedMessage,
                  inlineData,
                } as InlineDataConversationMessage;
              }

              this.emitMessage(
                conversationId,
                msg,
                'conversation-message-updated',
              );
            }

            if (outcome === 'channel_rac' || outcome === 'channel_email') {
              // set the context details after we've updated the cached message
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const value = (extra?.value as Record<string, any>) || {};

              if (outcome === 'channel_rac') {
                const userName = value?.rac_form?.name;
                const userPhoneNumber = value?.rac_form?.phone_number;
                this.emitNewConversationEvent({
                  name: 'conversation-set-context-values',
                  conversationId,
                  values: {
                    channel: 'request_a_call',
                    requestACallDetails: {
                      userName: userName || '',
                      userPhoneNumber: userPhoneNumber || '',
                      callCanceled: false,
                    },
                  },
                });
              }

              if (outcome === 'channel_email') {
                const email = value?.email_form.email;
                const body = value?.email_form.body;
                const ccEmails = value?.email_form.cc_emails;
                const attachmentIds = value?.email_form.attachment_ids;
                const issueContext = value?.email_form.issue_context;

                this.emitNewConversationEvent({
                  name: 'conversation-set-context-values',
                  conversationId,
                  values: {
                    channel: 'email',
                    emailDetails: {
                      email: email || '',
                      body: body || '',
                      ccEmails: ccEmails || [],
                      attachmentIds: attachmentIds || [],
                      issueContext: issueContext || {},
                    },
                  },
                });
              }
            }

            if (this.enableUhPendingInput) {
              await this.handleEffects(conversationId, resp.effects, true);
            } else {
              this.handleEffects(conversationId, resp.effects, true);
            }
          } else if (resp.type === 'error') {
            let code = 400;
            const {error} = resp;
            if (resp.error === UNAUTHORIZED_ERROR) {
              code = 401;
            }
            return {code, error};
          } else {
            return {code: 400, error: resp.type};
          }
        }
      } else if (action.name === 'thumbs-down-advance') {
        // if there was a thumbs down advance, we need to hide options not yet selected
        // b/c once we advance the converation, those options will error out on the api
        // and break the linear flow of the conversation.
        this.messageCache.forEach((message, key) => {
          if (!key.startsWith(`${conversationId}-`)) return;
          if (!shouldHideMessageAfterThumbsDownAdvance(message)) return;
          if (isButtonOptionsMessage(message) && message.selectedOptionId) {
            return;
          }

          if (isButtonOptionsMessage(message)) {
            this.emitMessage(
              conversationId,
              {
                ...message,
                status: ConversationMessageStatus.Sent,
                selectedOptionId: 'feedback_thumbs_down_advance',
              },
              'conversation-message-updated',
            );
          } else if (isInlineDataMessage(message)) {
            this.emitMessage(
              conversationId,
              {
                ...message,
                status: ConversationMessageStatus.Sent,
                shouldHideAfterThumbsDown: true,
              },
              'conversation-message-updated',
            );
          }
        });

        const resp = await this.advanceConversation(conversationId, {
          feedback: 'thumbs_down',
          optionId: 'feedback_thumbs_down_advance',
          data_with_pii: action.data ?? undefined,
        });

        if (resp.type === 'success') {
          if (this.enableUhPendingInput) {
            await this.handleEffects(conversationId, resp.effects, true);
          } else {
            this.handleEffects(conversationId, resp.effects, true);
          }
        } else if (resp.type === 'error' && resp.error === UNAUTHORIZED_ERROR) {
          return {code: 401, error: resp.error};
        } else {
          return {code: 400, error: resp.type};
        }
      }
      return {code: 200};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = this.reportAndEmitException(
        `exception sending conversation action: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  public waitForPendingMessagesHandled(): Promise<boolean> {
    return firstValueFrom(
      this.pending$.pipe(
        filter((pending) => !pending),
        debounceTime(250),
      ),
    );
  }

  busyObservable(): Observable<boolean> {
    return this.pending$.asObservable();
  }

  public isPending(): boolean {
    return this.pending$.value;
  }

  fetchEffects = async (
    conversationId: string,
  ): Promise<Array<UniversalHelpEffect>> => {
    const inProgressResp = await this.inProgressConversation({
      conversationId,
    });
    if (inProgressResp.type !== 'conversation_exists') {
      return [];
    }

    const {effects} = inProgressResp;

    return effects;
  };

  protected async handleInvalidOption(
    conversationId: string,
    lastEffectId: string,
  ): Promise<ClientResponse> {
    const effects = await this.fetchEffects(conversationId);
    if (effects.length > 0 && effects[effects.length - 1].id === lastEffectId) {
      throw new Error('Invalid option submitted but conversation was in sync');
    }

    // this should re-sync the conversation in this scenario
    await this.handleEffects(conversationId, effects);

    return this.error(400, 'invalid-option-for-conversation');
  }

  protected async handleEffects(
    conversationId: string,
    effects: UniversalHelpEffect[],
    waitForAnimation = false,
  ) {
    this.emitNewConversationEvent({
      name: 'conversation-typing-ended',
      conversationId,
      participantId: UniversalHelpUser.id,
    });

    await this.effectsToMessages(conversationId, effects, waitForAnimation);

    if (this.enableUhPendingInput) {
      await this.handleMostRecentEffect(conversationId, effects);
    } else {
      this.handleMostRecentEffect(conversationId, effects);
    }
  }

  protected async handleMostRecentEffect(
    conversationId: string,
    effects: UniversalHelpEffect[],
  ) {
    if (effects.length === 0) {
      return;
    }

    const nextEffect = effects[effects.length - 1];

    if (nextEffect.type.startsWith('continue_immediately')) {
      this.emitNewConversationEvent({
        name: 'conversation-typing-started',
        conversationId,
        participantId: UniversalHelpUser.id,
      });

      await this.advanceConversation(conversationId, {
        optionId: (nextEffect as UniversalHelpEffectContinueImmediately)
          .immediateNextEdge as string,
      }).then((resp) => {
        if (resp.type === 'success') {
          return this.handleEffects(conversationId, resp.effects, true);
        } else if ('error' in resp) {
          throw new Error(`Error advancing conversation: ${resp.error}`);
        }
      });
    } else if (nextEffect.type === 'begin_csat') {
      this.endConversation(conversationId);
    } else if (nextEffect.type === 'transition_to_chat') {
      this.emitNewConversationEvent({
        name: 'conversation-set-context-values',
        conversationId,
        values: {
          intent: 'contact_support',
          channel: 'chat',
          requestedChannel: 'chat',
        },
      });
    } else if (nextEffect.type === 'wait_for_response') {
      // Only enable attachments for logged-in users
      if (
        this.isLoggedIn &&
        nextEffect.allowAttachments &&
        nextEffect.allowTextInput
      ) {
        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {enabledInputModes: ['text', 'prompt-option', 'attachments']},
        });
      } else if (nextEffect.allowTextInput) {
        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {enabledInputModes: ['text', 'prompt-option']},
        });
      } else {
        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {enabledInputModes: ['prompt-option']},
        });
      }
    } else if (nextEffect.type === 'show_inline_data') {
      if (
        this.enableUhPendingInput &&
        nextEffect.inlineDataName === 'stripe_assistant'
      ) {
        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {enabledInputModes: ['text']},
        });
      } else {
        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {enabledInputModes: []},
        });
      }
    }
  }

  private emitMessage = (
    conversationId: string,
    message: ConversationMessage,
    eventName:
      | 'conversation-message-added'
      | 'conversation-message-updated' = 'conversation-message-added',
  ): void => {
    this.cacheMessage(conversationId, message);
    this.emitNewConversationEvent({
      name: eventName,
      conversationId,
      participantId: message.participant.id,
      messageId: message.id,
      message,
    });
  };

  protected getStripeRegion(): string {
    return this.userCountry === 'IN' ? 'bom' : 'northwest';
  }

  protected createConversation = async (
    body: UniversalHelpCreateEndpointRequest,
  ): Promise<
    | UniversalHelpCreateEndpointResponseError
    | UniversalHelpCreateEndpointResponseSuccess
  > => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.enableRegionHeaderRouting) {
      headers['stripe-region'] = this.getStripeRegion();
    }
    const fetched = await this.fetch(`${this.conversationUrl}/create`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers,
    });

    if (fetched.status === 401) {
      return UNAUTHORIZED_RESPONSE;
    }

    const created: UniversalHelpCreateEndpointResponse = await fetched.json();
    if (created.type === 'error') {
      // unless this is a 401, something went wrong; throw
      if (fetched.status !== 401) {
        throw new Error(`Error creating conversation: ${created.error}`);
      }
    }
    return created;
  };

  advanceConversation = async (
    conversationId: string,
    body: UniversalHelpAdvanceEndpointRequest,
    {avoidTyping = false}: {avoidTyping?: boolean} = {},
  ): Promise<
    | UniversalHelpAdvanceEndpointResponseSuccess
    | UniversalHelpAdvanceEndpointResponseClientError
    | UniversalHelpAdvanceEndpointResponseError
  > => {
    try {
      if (!this.enableUhPendingInput) {
        this.emitNewConversationEvent({
          name: 'conversation-set-context-values',
          conversationId,
          values: {enabledInputModes: []},
        });
      }

      // wait until animations can finish + breather
      await this.wait(ANIMATION_DURATION_WITH_BUFFER);

      if (!avoidTyping) {
        this.emitNewConversationEvent({
          name: 'conversation-typing-started',
          conversationId,
          participantId: UniversalHelpUser.id,
        });
      }

      const params = new URLSearchParams();
      params.set('conversationId', conversationId);
      const fetched = await this.fetch(
        `${this.conversationUrl}/advance?${params.toString()}`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (fetched.status === 401) {
        this.emitNewConversationEvent({
          name: 'conversation-typing-ended',
          conversationId,
          participantId: UniversalHelpUser.id,
        });

        return UNAUTHORIZED_RESPONSE;
      }

      const advanced: UniversalHelpAdvanceEndpointResponse =
        await fetched.json();

      if (advanced.type === 'error') {
        this.emitNewConversationEvent({
          name: 'conversation-typing-ended',
          conversationId,
          participantId: UniversalHelpUser.id,
        });

        // unless this is a 401, something went wrong; throw
        if (fetched.status !== 401) {
          throw new Error(`Error advancing conversation: ${advanced.error}`);
        }
      }

      return advanced;
    } catch (e) {
      if (isNetworkError(e)) {
        // re-throw and catch higher up
        throw e;
      } else {
        const error = `exception advancing conversation: ${
          (e as Error).message
        }`;
        this.emitNewEvent({
          name: 'client-exception',
          error,
        });

        return {
          type: 'error',
          error,
        };
      }
    }
  };

  async wait(duration: number): Promise<void> {
    await new Promise((resolve) => {
      setTimeout(resolve, duration);
    });
  }

  protected inProgressConversation = async ({
    conversationId,
    audience,
    entryPoint,
    triggerPoint,
  }: {
    conversationId?: string;
    audience?: string;
    entryPoint?: string;
    triggerPoint?: string;
  } = {}): Promise<
    | UniversalHelpInProgressEndpointResponseConversationExists
    | UniversalHelpInProgressEndpointResponseNoConversation
    | UniversalHelpInProgressEndpointResponseError
  > => {
    const params = new URLSearchParams();
    if (conversationId) {
      params.set('conversationId', conversationId);
    }
    if (audience) {
      params.set('audience', audience);
    }
    if (entryPoint) {
      params.set('entryPoint', entryPoint);
    }
    if (triggerPoint) {
      params.set('triggerPoint', triggerPoint);
    }
    const fetched = await this.fetch(
      `${this.conversationUrl}/in-progress?${params.toString()}`,
    );

    if (fetched.status === 401) {
      return UNAUTHORIZED_RESPONSE;
    }

    const inProgress: UniversalHelpInProgressEndpointResponse =
      await fetched.json();
    if (inProgress.type === 'error') {
      // unless this is a 401, something went wrong; throw
      if (fetched.status !== 401) {
        throw new Error(
          `Error fetching in progress conversation: ${inProgress.error}`,
        );
      }
    }

    return inProgress;
  };

  private endConversation = async (
    conversationId: string,
  ): Promise<
    | UniversalHelpEndEndpointResponseSuccess
    | UniversalHelpEndEndpointResponseErrorWithCode
  > => {
    if (!conversationId) {
      return {code: 400, type: 'error', error: 'conversationId is required'};
    }
    this.emitNewConversationEvent({
      name: 'conversation-typing-ended',
      conversationId,
      participantId: UniversalHelpUser.id,
    });
    this.emitNewConversationEvent({
      conversationId,
      name: 'conversation-updated',
      status: ConversationStatus.Inactive,
    });

    const params = new URLSearchParams();
    params.set('conversationId', conversationId);

    const fetched = await this.fetch(
      `${this.conversationUrl}/end?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (fetched.status === 401) {
      return UNAUTHORIZED_RESPONSE;
    }

    const resp = await fetched.json();
    if (resp.type === 'error') {
      this.errors.reportWarning(`error ending conversation: ${resp.error}`, {
        tags: ERROR_TAGS,
      });
    }
    return resp;
  };

  // iterate effects and emit messages from them, returning what messages were mapped
  protected async effectsToMessages(
    conversationId: string,
    effects: Array<UniversalHelpEffect>,
    waitForAnimation = false,
  ): Promise<Record<string, ConversationMessage>> {
    const messages: Record<string, ConversationMessage> = {};

    if (effects.length === 0) {
      return messages;
    }

    const msgPromises: Promise<boolean>[] = [];

    let i = 0;
    const delayDuration = ANIMATION_DURATION + 100;

    const emitMessageWithAnimation = (message: ConversationMessage): void => {
      messages[message.id] = message;
      if (waitForAnimation && this.waitForAnimationEnabled) {
        if (msgPromises.length === 0) {
          this.emitMessage(conversationId, message);
          msgPromises.push(Promise.resolve(true));
        } else {
          msgPromises.push(
            new Promise((resolve) => {
              setTimeout(() => {
                this.emitMessage(conversationId, message);
                resolve(true);
              }, delayDuration * i);
            }),
          );
        }
      } else {
        this.emitMessage(conversationId, message);
      }
    };

    effects.forEach((effect, idx) => {
      switch (effect.type) {
        case 'option_was_selected': {
          if (
            effect.optionId === 'text-input' &&
            effect.messageOverride &&
            effect.messageOverride.trim() !== ''
          ) {
            i += 1;
            const message: TextConversationMessage = {
              id: effect.id,
              type: 'text-conversation-message',
              timestamp: Date.now() + i,
              participant: this.user,
              text: effect.messageOverride,
            };
            emitMessageWithAnimation(message);
          } else if (
            effect.optionId === 'text-with-attachments-input' &&
            effect.messageOverride &&
            effect.messageOverride.trim() !== ''
          ) {
            i += 1;
            const attachments = (effect.attachments || []).map((a) => ({
              name: a.name,
              size: a.size,
              type: a.type,
              upload_token: a.uploadToken,
            }));
            const message: TextWithAttachmentsConversationMessage = {
              id: effect.id,
              type: 'text-with-attachments-conversation-message',
              timestamp: Date.now() + i,
              participant: this.user,
              text: effect.messageOverride,
              attachments,
            };
            emitMessageWithAnimation(message);
          } else if (
            effect.optionId === 'text-input' &&
            effect.messageOverride?.trim() === ''
          ) {
            this.errors.reportWarning(
              'conversation received a text-input option_was_selected with an empty message',
              {
                tags: {
                  ...ERROR_TAGS,
                  conversationId,
                  effectId: effect.id,
                },
              },
            );
          }
          break;
        }
        case 'wait_for_response': {
          i += 1;
          const msgId = effect.id;

          const options = effect.optionsForUser.map((effectOption) => {
            return {
              id: effectOption.id,
              label: effectOption.message,
              clickInterceptUrl: effectOption.clickInterceptUrl,
              externalUrl: effectOption.externalUrl,
            };
          });

          const {purpose} = effect;

          let message: ConversationMessage | undefined;

          const emptyPrompt = options.length === 0;

          let selectedOptionId: string | undefined;
          if (idx < effects.length - 1) {
            const nextEffect = effects[idx + 1];
            if (nextEffect.type === 'option_was_selected') {
              selectedOptionId = nextEffect.optionId;
            }
          }

          const participant = this.user || UniversalHelpUser;

          let promptMessage: ButtonOptionsConversationMessage | undefined;

          if (effect.selectStyle === 'dropdown') {
            message = {
              id: msgId,
              type: 'rich-text-conversation-message',
              timestamp: Date.now() + i,
              participant: UniversalHelpUser,
              text: effect.messageForUser as string,
              data: {
                type: 'select-options-conversation-message',
                id: msgId,
                options,
                purpose,
                selectedOptionId,
                defaultOptionId: effect.preselectedOptionId,
                responseId: effect.responseId,
                feedback: effect.feedback,
              },
            };
          } else {
            if (effect.messageForUser) {
              this.errors.reportWarning(
                'wait-for-response type included a message for user',
                {
                  tags: ERROR_TAGS,
                },
              );
              message = {
                id: `${msgId}-message-for-user`,
                type: 'text-conversation-message',
                timestamp: Date.now() + i,
                participant: UniversalHelpUser,
                text: effect.messageForUser as string,
                responseId: effect.responseId,
                feedback: effect.feedback,
              };
            }

            if (!emptyPrompt) {
              promptMessage = {
                id: msgId,
                type: 'button-options-conversation-message',
                timestamp: Date.now() + i,
                participant,
                purpose,
                options,
                selectedOptionId,
                recommendedOptionId: effect.recommendedOptionId,
                responseId: effect.responseId,
                feedback: effect.feedback,
              };
            }
          }

          if (message) {
            i += 1;
            emitMessageWithAnimation(message);
          }

          if (promptMessage) {
            emitMessageWithAnimation(promptMessage);
          }
          break;
        }
        case 'show_inline_data': {
          // important to note that i and idx are not the same
          // i represents how many messages we've iterated
          // idx represents the index of effects and is used
          // below to determine the last effect in the array
          i += 1;

          // We assume that done/advance was already called for this inline data if it isn't the most recent effect.
          // This helps avoid calling advance multiple times for the same inline data.
          const isDone = idx < effects.length - 1;

          const inlineData = {
            name: effect.inlineDataName,
            style: effect.style,
            isDone,
            data: effect.rendering as Array<ConversationInlineDatum>,
          } as ConversationInlineData;

          let message:
            | RichTextConversationMessage
            | InlineDataConversationMessage;
          if (effect.messageForUser) {
            message = {
              id: effect.id,
              type: 'rich-text-conversation-message',
              timestamp: Date.now() + i,
              participant: UniversalHelpUser,
              text: effect.messageForUser as string,
              data: {
                type: 'inline-data-conversation-message',
                effectId: effect.id,
                inlineData,
              },
              responseId: effect.responseId,
              feedback: effect.feedback,
            } as RichTextConversationMessage;
          } else {
            message = {
              id: effect.id,
              type: 'inline-data-conversation-message',
              timestamp: Date.now() + i,
              participant: UniversalHelpUser,
              effectId: effect.id,
              inlineData,
              responseId: effect.responseId,
              feedback: effect.feedback,
            } as InlineDataConversationMessage;
          }

          emitMessageWithAnimation(message);
          break;
        }
        case 'continue_immediately': {
          if (effect.messageForUser) {
            i += 1;
            const message: TextConversationMessage = {
              id: effect.id,
              type: 'text-conversation-message',
              timestamp: Date.now() + i,
              participant: UniversalHelpUser,
              text: effect.messageForUser as string,
              style: (effect.messageStyle ||
                'bubble') as TextConversationMessage['style'],
              responseId: effect.responseId,
              feedback: effect.feedback,
            };
            emitMessageWithAnimation(message);
          }
          break;
        }
        case 'continue_immediately_with_articles': {
          i += 1;

          const articles: ArticlesMessage = {
            type: 'articles-conversation-message',
            articleStyle: effect.articleStyle,
            articles: effect.articlesForUser.map((article) => {
              return {
                id: article.url, // each article should have a unique URL
                title: article.title,
                url: article.url,
                collection: article.collection,
              };
            }),
          };

          let message:
            | RichTextConversationMessage
            | ArticlesConversationMessage;

          if (!!effect.messageForUser && effect.messageForUser.trim() !== '') {
            message = {
              id: effect.id,
              type: 'rich-text-conversation-message',
              timestamp: Date.now() + i,
              participant: UniversalHelpUser,
              text: effect.messageForUser as string,
              data: articles,
              responseId: effect.responseId,
              feedback: effect.feedback,
            };
          } else {
            message = {
              ...articles,
              id: effect.id,
              timestamp: Date.now() + i,
              participant: UniversalHelpUser,
              responseId: effect.responseId,
              feedback: effect.feedback,
            };
          }

          emitMessageWithAnimation(message);
          break;
        }
        case 'continue_immediately_with_buttons': {
          i += 1;

          const buttons: ButtonsMessage = {
            type: 'buttons-conversation-message',
            buttons: effect.buttonsForUser.map((button) => {
              return {
                title: button.title,
                url: button.url,
              };
            }),
          };

          const message: RichTextConversationMessage = {
            id: effect.id,
            type: 'rich-text-conversation-message',
            timestamp: Date.now() + i,
            participant: UniversalHelpUser,
            text: effect.messageForUser as string,
            data: buttons,
            responseId: effect.responseId,
            feedback: effect.feedback,
          };

          emitMessageWithAnimation(message);
          break;
        }
        default: {
          // no-op
        }
      }
    });

    await Promise.all(msgPromises);

    return messages;
  }

  protected cacheMessage(
    conversationId: string,
    message: ConversationMessage,
  ): void {
    const key = `${conversationId}-${message.id}`;
    this.messageCache.set(key, message);
  }

  protected getCachedMessage(
    conversationId: string,
    messageId: string,
  ): ConversationMessage | undefined {
    const key = `${conversationId}-${messageId}`;
    return this.messageCache.get(key);
  }

  protected clearCachedMessage(
    conversationId: string,
    messageId: string,
  ): void {
    const key = `${conversationId}-${messageId}`;
    this.messageCache.delete(key);
  }

  protected error = async (
    code: number,
    error: string,
    tags?: ErrorScope['tags'],
  ): Promise<ClientErrorResponse> => {
    return super.error(code, error, {...ERROR_TAGS, ...tags});
  };

  private validExistingInProgressResponse(
    params: StartInProgressConversationParams,
  ):
    | UniversalHelpInProgressEndpointResponseConversationExists
    | UniversalHelpInProgressEndpointResponseNoConversation
    | null {
    const {conversationId, inProgressResponse} = params;

    if (!inProgressResponse || 'error' in inProgressResponse) {
      return null;
    }

    const {data} = inProgressResponse;
    const {
      conversationId: inProgressConversationId,
      channel,
      clientType,
      extra,
      inProgress,
    } = data;

    if (conversationId && conversationId !== inProgressConversationId) {
      return null;
    }

    const isValid =
      channel === 'bot' &&
      clientType === this.type &&
      inProgressConversationId &&
      !!extra?.effects;

    if (!isValid) {
      return null;
    }

    if (inProgress) {
      return {
        type: 'conversation_exists',
        conversationId: inProgressConversationId,
        effects: extra.effects as Array<UniversalHelpEffect>,
      };
    }

    return {type: 'no_conversation'};
  }

  protected incrementAndTrack(
    name: string,
    params: Record<string, string> = {},
  ): void {
    const metricParams = {...METRIC_TAGS, ...params};
    this.metrics.increment(name, metricParams);
    this.analytics.track(name, metricParams);
  }

  private async reportAndEmitException(
    error: string,
    tags?: ErrorScope['tags'],
  ): Promise<ClientErrorResponse> {
    const clientErrorResponse = await this.error(500, error, {
      ...tags,
      assistant_error_screen: true,
    });
    this.emitNewEvent({
      name: 'client-exception',
      error,
      sentryEventId: clientErrorResponse.sentryEventId,
    });

    return clientErrorResponse;
  }

  async sendFeedback({
    conversationId,
    responseId,
    feedback,
  }: SendFeedbackParams): Promise<ClientResponse> {
    const payload: UniversalHelpResponseFeedbackEndpointRequest = {
      feedback_value: feedback,
      conversation_id: conversationId,
      response_id: responseId,
    };
    return this.fetch(`${this.conversationUrl}/response-feedback`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {'Content-Type': 'application/json'},
    })
      .then((r) => {
        this.incrementAndTrack(METRIC.send_feedback_success);
        return {
          code: r.status,
          data: {success: r.status === 204},
          error:
            r.status !== 204
              ? 'Unexpected response when posting feedback'
              : undefined,
        };
      })
      .catch(async (e) => {
        if (isNetworkError(e)) {
          return this.error(408, (e as Error).message);
        }

        const clientErrorResponse = await this.reportAndEmitException(
          `exception sending feedback: ${(e as Error).message}`,
        );

        this.incrementAndTrack(METRIC.send_feedback_error);

        return clientErrorResponse;
      });
  }
}
